import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { ProxyGraphClients, abgGraphqlClient } from '@atlassian/cssxp-client-utils';
import { getNotificationPreferencesByTransactionAccountId, setNotificationPreferences } from '@atlassian/temp-proxies';
import {
  NotificationTypes,
  errorNotificationBuilder,
  successNotificationBuilder,
  useApplicationContext,
  useNotification,
} from '@css-xp/base';
import { getSupportUrl } from '@css-xp/ppc-common';
import {
  FetchBillingAdminsForTransactionAccountPpcQuery,
  FetchBillingAdminsForTransactionAccountPpcQueryVariables,
} from '@css-xp/ppc-graphtypes';
import { fetchBillingAdminsForTransactionAccountPPC } from '../../../../../queries';
import { NOTIFICATION_DISMISS_TIME } from '../../constants';

export enum NotificationPreferenceTypes {
  EMAIL = 'email',
}

export enum NotificationLevel {
  INVOICE_GROUP = 'invoice-group',
}

interface getNotificationPreferencesResponseParams {
  userAaid: string;
  entitlementId?: null;
  transactionAccountId: string;
  notificationPreferences: [
    {
      type: 'email';
      enabled: boolean;
      level?: string;
    }
  ];
}

const ppcBffGraphqlClient = abgGraphqlClient(ProxyGraphClients.PPC_BFF);

const errorFlag = (
  <div>
    Try again. If this keeps happening, contact <br />
    {getSupportUrl()}
  </div>
);

export const useBillingAdminsPreferencesHandler = (billingAdminIds: string[] = []) => {
  const { txaId } = useParams<{ txaId: string }>();
  const partnerAccountData = useApplicationContext();
  const selectedPartnerAccount = partnerAccountData?.partnerAccounts?.find(
    ({ transactionAccountId }: { transactionAccountId: string }) => transactionAccountId === txaId
  );

  const { sendSuccessNotification, sendErrorNotification, setNotificationSent } = useNotification();

  const successNotificationOptions = successNotificationBuilder()
    .withId('ppc-notification-preferences-success')
    .withDismissAfter(NOTIFICATION_DISMISS_TIME)
    .isVersion(NotificationTypes.FLAG);

  const errorNotificationOptions = errorNotificationBuilder()
    .withId('ppc-notification-preferences-error')
    .withDismissAfter(NOTIFICATION_DISMISS_TIME)
    .isVersion(NotificationTypes.FLAG);

  const { data, loading, error } = useQuery<
    FetchBillingAdminsForTransactionAccountPpcQuery,
    FetchBillingAdminsForTransactionAccountPpcQueryVariables
  >(fetchBillingAdminsForTransactionAccountPPC, {
    variables: {
      id: txaId,
      billingAdminOptions: {
        partnerAccountId: selectedPartnerAccount?.id as string,
        billingAdminIds,
      },
    },
    client: ppcBffGraphqlClient,
    displayName: `fetchBillingAdmins`,
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-first',
  });

  const getAdminsPreferencesByTxaId = async (type: string, shouldManageNotificationAtBillingProfileLevel?: boolean) => {
    try {
      const billingAdminsPreferences = await getNotificationPreferencesByTransactionAccountId(txaId);

      if (!billingAdminsPreferences) {
        return [];
      }

      if (shouldManageNotificationAtBillingProfileLevel) {
        const selectedPreferences = billingAdminsPreferences?.userPreference?.map(
          (admin: getNotificationPreferencesResponseParams) => {
            const specificPref = admin.notificationPreferences.find((pref) => pref.type === type);
            return {
              userAaid: admin?.userAaid,
              isEnabled: specificPref?.enabled,
              level: specificPref?.level,
            };
          }
        );
        return selectedPreferences;
      } else {
        // creating an array of all the AAIDs who have opted out of receiving notifications of a specific type.
        const selectedAdminsAaids = billingAdminsPreferences?.userPreference
          ?.filter((admin: getNotificationPreferencesResponseParams) =>
            admin.notificationPreferences.some((pref) => pref.type === type && pref.enabled === false)
          )
          .map((admin: getNotificationPreferencesResponseParams) => admin.userAaid);

        return selectedAdminsAaids;
      }
    } catch (error) {
      const options = errorNotificationOptions
        .withHeader("We're unable to update the preferences")
        .withMessage(errorFlag)
        .build();
      sendErrorNotification(options);
      setNotificationSent(true);
      return [];
    }
  };

  const setAdminsPreferences = async (
    aaid: string,
    type: string,
    hasEnabled: boolean,
    shouldManageNotificationAtBillingProfileLevel?: boolean,
    level?: string | null
    // eslint-disable-next-line max-params
  ) => {
    try {
      const response = await setNotificationPreferences([
        {
          userAaid: aaid,
          transactionAccountId: txaId,
          notificationPreferences: [
            {
              type: type,
              enabled: hasEnabled,
              ...(shouldManageNotificationAtBillingProfileLevel && level ? { level: level } : {}),
            },
          ],
        },
      ]);
      const options = successNotificationOptions
        .withHeader('Success')
        .withMessage('Your preferences have been updated successfully.')
        .build();
      sendSuccessNotification(options);
      setNotificationSent(true);
      return { success: true, response };
    } catch (error) {
      const options = errorNotificationOptions
        .withHeader("We're unable to update the preferences")
        .withMessage(errorFlag)
        .build();
      sendErrorNotification(options);
      setNotificationSent(true);
      return { success: false, error };
    }
  };

  return { data, loading, error, getAdminsPreferencesByTxaId, setAdminsPreferences };
};
